import LocaleService from 'services/loader-handler-services/locale-service';

// StateFormatService
//
// Display projections for state variables.
//
// A variable has one stored value, a number, and any number of display
// projections of it. Twine content needed a second variable per displayed value
// ($total and $totalDisplay) because `formatNumber` returns a string you can no
// longer do arithmetic on. Here the format belongs to whoever displays the
// value, so the stored value stays numeric and one variable can render as
// currency in one place and a bare number in another.
//
// Formats are named, so a content author picks one from a list in Forge and
// never hand-writes Intl options.

const DEFAULT_CURRENCY = 'USD';
const DEFAULT_LOCALE = 'en-US';

// Formats that carry a decimal precision, with the precision they default to
// when the author doesn't give one.
const DEFAULT_PRECISION = {
  currency: 2,
  decimal: 2,
  number: 2,
  percent: 0,
  integer: 0,
};

const StateFormatService = {
  // Named formats. Each receives the already-coerced number plus the resolved
  // options, and returns a display string.
  formats: {
    currency: function (value, options) {
      return value.toLocaleString(options.locale, {
        style: 'currency',
        currency: options.currency,
        minimumFractionDigits: options.precision,
        maximumFractionDigits: options.precision,
      });
    },

    // Note this expects a whole percentage (45 renders as "45%"), not a
    // fraction, because that is how twine content stores these values.
    percent: function (value, options) {
      return `${value.toLocaleString(options.locale, {
        minimumFractionDigits: options.precision,
        maximumFractionDigits: options.precision,
      })}%`;
    },

    decimal: function (value, options) {
      return value.toLocaleString(options.locale, {
        minimumFractionDigits: options.precision,
        maximumFractionDigits: options.precision,
      });
    },

    integer: function (value, options) {
      return Math.round(value).toLocaleString(options.locale);
    },
  },

  // `number` is an alias for `decimal`; authors reach for either name.
  aliases: {
    number: 'decimal',
  },

  // Resolve the format name an author wrote to a formatter function.
  getFormatter: function (format) {
    const name = this.aliases[format] || format;
    return this.formats[name];
  },

  isSupported: function (format) {
    return this.getFormatter(format) !== undefined;
  },

  // The locale to format in. Follows the learner's course locale so a Spanish
  // enrollment gets Spanish number grouping without the content asking for it,
  // which is what the hardcoded 'en-US' in every twine `formatNumber` call
  // prevented.
  //
  // `LocaleService.locale` only carries a value for a language tag with a region
  // in it (`es-MX`); `setFileConfigData` leaves it undefined for a plain `es`.
  // `lang` is always set, and Intl accepts it, so it goes in between. Without
  // it every region-less course formats as en-US.
  getLocale: function () {
    return LocaleService.locale || LocaleService.lang || DEFAULT_LOCALE;
  },

  resolveOptions: function (format, options = {}) {
    const name = this.aliases[format] || format;
    return {
      locale: options.locale || this.getLocale(),
      currency: options.currency || DEFAULT_CURRENCY,
      precision: options.precision !== undefined ? options.precision : DEFAULT_PRECISION[name],
    };
  },

  // Project a stored value for display. An unformatted or unrecognized format
  // falls through to the value's own string form, so a missing/misspelled
  // format still leaves a readable page.
  format: function (value, format, options) {
    if (!format) {
      return this.toDisplayString(value);
    }

    const formatter = this.getFormatter(format);
    if (!formatter) {
      console.error(`StateFormatService: unknown format '${format}'`);
      return this.toDisplayString(value);
    }

    const number = this.toNumber(value);
    if (number === undefined) {
      // Formatting a non-numeric value (an unset variable, or a string that
      // was never meant to be a number) would render "NaN" on the page.
      return this.toDisplayString(value);
    }

    return formatter(number, this.resolveOptions(format, options));
  },

  toNumber: function (value) {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  },

  toDisplayString: function (value) {
    if (value === undefined || value === null) {
      return '';
    }
    if (Array.isArray(value) || typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  },
};

export default StateFormatService;
